import React from 'react';
import { ReferenceListProps } from '../types';
import { KEYBOARD_KEYS } from '../utils/accessibility';
import './ReferenceList.css';

interface ExtendedReferenceListProps extends ReferenceListProps {
  messageId?: string;
}

const ReferenceList: React.FC<ExtendedReferenceListProps> = ({
  references,
  onReferenceClick,
  messageId = 'default'
}) => {
  if (!references || references.length === 0) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, ref: any) => {
    if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.SPACE) {
      e.preventDefault();
      onReferenceClick(ref);
    }
  };

  const getTypeIcon = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'image':
      case 'img':
      case 'png':
      case 'jpg':
      case 'jpeg':
        return '🖼️';
      case 'video':
      case 'mp4':
      case 'avi':
        return '🎥';
      case 'audio':
      case 'mp3':
      case 'wav':
        return '🎵';
      case 'link':
      case 'url':
      case 'web':
        return '🔗';
      case 'code':
      case 'js':
      case 'ts':
      case 'py':
        return '💻';
      default:
        return '📋';
    }
  };

  const listId = `references-${messageId}`;
  const headingId = `references-heading-${messageId}`;

  return (
    <section 
      className="reference-list" 
      role="region" 
      aria-labelledby={headingId}
    >
      <div className="reference-list__header">
        <h4 
          id={headingId}
          className="reference-list__title"
          role="heading"
          aria-level={4}
        >
          References
        </h4>
        <span 
          className="reference-list__count"
          aria-label={`${references.length} reference${references.length !== 1 ? 's' : ''}`}
        >
          ({references.length})
        </span>
      </div>
      <ul 
        id={listId}
        className="reference-list__items"
        role="list"
        aria-label="Reference documents"
      >
        {references.map((ref, index) => (
          <li key={ref.id} role="listitem">
            <button
              className="reference-list__item accessible-button"
              onClick={() => onReferenceClick(ref)}
              onKeyDown={(e) => handleKeyDown(e, ref)}
              type="button"
              aria-label={`Open ${ref.type} reference: ${ref.title}`}
              aria-describedby={`ref-desc-${messageId}-${index}`}
            >
              <span className="reference-list__icon" aria-hidden="true">
                {getTypeIcon(ref.type)}
              </span>
              <div className="reference-list__content">
                <span className="reference-list__item-title">{ref.title}</span>
                <span 
                  id={`ref-desc-${messageId}-${index}`}
                  className="reference-list__item-type"
                >
                  {ref.type.toUpperCase()}
                </span>
              </div>
              <span className="reference-list__arrow" aria-hidden="true">→</span>
              <span className="sr-only">
                Press Enter or Space to open
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ReferenceList;